import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useCallback } from 'react';

// @mui
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Stack,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  Autocomplete,
  DialogActions,
  DialogContent
} from '@mui/material';

// components

// services
import { debounce } from 'lodash';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useGetMyCompaniesLeanQuery } from 'src/services/connections/connections.service';
import { useGetMyCandidatesLeanQuery } from 'src/services/candidates/candidates.service';
import { useGetOrganizationContactsLeanQuery } from 'src/services/contacts/contacts.service';
import { PICKER_PAGE, pickerSortConfig, pickerHelperText, pickerNoOptionsText, buildPickerColumnFilters } from 'src/utils/picker-search';

// types
import { ListEntityType } from '../../../services/lists/lists.types';
import { useGetListByIdQuery, useAddListItemsMutation } from '../../../services/lists/lists.service';

// ----------------------------------------------------------------------

interface Props {
  open: boolean;
  listId: string | null;
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
}

interface SelectableOption {
  id: string;
  name: string;
}

export default function AddToListModal({ open, onClose, listId, onSuccess }: Props) {

  const { t } = useTranslation();

  const authContext = useAuthContext();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<SelectableOption[]>([]);

  const [addItems, { isLoading: isAddingToList }] = useAddListItemsMutation();

  const { data: currentList } = useGetListByIdQuery(listId as string,
    { skip: !listId }
  );

  // Name search (email too for candidates and contacts) on the lean lists, only while open and only for
  // the type this list holds (JT1-1586). The full list search scanned notes and timed out on large orgs.
  const page = PICKER_PAGE;
  const sortConfig = pickerSortConfig(searchQuery);
  const personColumnFilters = buildPickerColumnFilters(searchQuery, { email: true });

  const contactsQuery = useGetOrganizationContactsLeanQuery({
    org_id: authContext.organization.id,
    page,
    sort_config: sortConfig,
    column_filters: personColumnFilters,
  }, {
    skip: !open || currentList?.entity_type !== ListEntityType.CONTACT,
  });

  const candidatesQuery = useGetMyCandidatesLeanQuery({
    page,
    sortConfig,
    column_filters: personColumnFilters,
  }, {
    skip: !open || currentList?.entity_type !== ListEntityType.CANDIDATE,
  });

  const companiesQuery = useGetMyCompaniesLeanQuery({
    params: {
      ...page,
      ...sortConfig,
      column_filters: buildPickerColumnFilters(searchQuery),
    }
  }, {
    skip: !open || currentList?.entity_type !== ListEntityType.COMPANY,
  });

  const onSearch = useMemo(() => debounce((value: string) => {
    setSearchQuery(value);
  }, 500), [setSearchQuery]);

  const handleClose = useCallback(() => {
    setSearchQuery('');
    setErrorMessage('');
    setSelectedItems([]);
    onClose();
  }, [onClose]);

  const handleAddToList = useCallback(async () => {
    setErrorMessage('');

    if (selectedItems.length === 0) {
      setErrorMessage(t('lists.add_to_list_modal.errors.empty_selection'));
      return;
    }

    try {
      await addItems({
        list_id: listId as string,
        data: {
          entities: selectedItems.map((item: any) => item.id),
        }
      }).unwrap();

      enqueueSnackbar(t('lists.api.add_to_list.success'), {
        variant: 'success',
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      enqueueSnackbar(t('lists.api.add_to_list.default_error'), {
        variant: 'error',
      });
    }
  }, [addItems, listId, selectedItems, t, onSuccess, handleClose]);

  const activeQuery = useMemo(() => {
    switch (currentList?.entity_type) {
      case ListEntityType.COMPANY:
        return companiesQuery;
      case ListEntityType.CONTACT:
        return contactsQuery;
      case ListEntityType.CANDIDATE:
        return candidatesQuery;
      default:
        return undefined;
    }
  }, [currentList, companiesQuery, contactsQuery, candidatesQuery]);

  const selectableOptions: SelectableOption[] = useMemo(() => {

    switch (currentList?.entity_type) {
      case ListEntityType.COMPANY:
        return companiesQuery.currentData?.results.map((company) => ({
          id: company.id,
          name: company.company_name,
        })) || [];
      case ListEntityType.CONTACT:
        return contactsQuery.currentData?.results.map((contact) => ({
          id: contact.id,
          name: [contact.first_name, contact.last_name].filter(Boolean).join(' '),
        })) || [];
      case ListEntityType.CANDIDATE:
        return candidatesQuery.currentData?.results.map((candidate) => ({
          id: candidate.id,
          name: [candidate.first_name, candidate.last_name].filter(Boolean).join(' '),
        })) || [];
      default:
        return [];
    }
  }, [companiesQuery.currentData, contactsQuery.currentData, candidatesQuery.currentData, currentList]);

  const inputLabel = useMemo(() => {
    switch (currentList?.entity_type) {
      case ListEntityType.COMPANY:
        return t('companies.profile.linked_companies.search_placeholder');
      case ListEntityType.CONTACT:
        return t('contacts.linked_contacts.search_placeholder');
      case ListEntityType.CANDIDATE:
        return t('contacts.detail.candidate_link.candidate_autocomplete.label');
      default:
        return t('common.search');
    }
  }, [currentList, t]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>
        {t('lists.add_to_list_modal.title')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={3}>
            <Autocomplete
              sx={{ flexGrow: 1 }}
              multiple
              options={selectableOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={!!activeQuery?.isFetching}
              noOptionsText={pickerNoOptionsText(t, !!activeQuery?.isError)}
              // Keep everything the server matched; only drop rows with no name to show.
              filterOptions={(options) => options.filter((option) => option.name)}
              value={selectedItems}
              onChange={(_, value) => setSelectedItems(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={inputLabel}
                  helperText={pickerHelperText(t, activeQuery?.currentData)}
                />
              )}
              // 'reset' is MUI re-syncing the input after a selection or re-render, not the user typing.
              onInputChange={(_, value, reason) => reason !== 'reset' && onSearch(value)}
              renderOption={({ key, ...rest }, option: SelectableOption) => (
                <li key={key} {...rest}>
                  <Stack direction="column">
                    <Typography variant="body1">
                      {`${option.name}`}
                    </Typography>
                  </Stack>
                </li>
              )}
            />
          </Stack>

          {errorMessage && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          {t('common.cancel')}
        </Button>

        <LoadingButton
          type="submit"
          variant="contained"
          onClick={handleAddToList}
          disabled={isAddingToList}
          loading={isAddingToList}
        >
          {t('common.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
